import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import type { Day } from "date-fns";

interface ScheduleGridProps {
  rrules: string;
  className?: string;
}

const DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

interface TimeSlot {
  from: string;
  to: string;
}

interface WeekSchedule {
  [day: string]: TimeSlot[];
}

function parseRRule(rrule: string): WeekSchedule {
  if (!rrule) return {};

  const schedule: WeekSchedule = {};
  const rules = rrule.split("\n").filter((r) => r.trim());

  rules.forEach((rule) => {
    const match = rule.match(
      /RRULE:FREQ=WEEKLY;BYDAY=([A-Z,]+);BYHOUR=(\d{1,2});BYMINUTE=(\d{1,2});DURATION=(\d+)/,
    );
    if (match) {
      const [, days, startHour, startMin, durationMin] = match;

      // Calculate end time from start time + duration
      const startMinutes = parseInt(startHour) * 60 + parseInt(startMin);
      const endMinutes = startMinutes + parseInt(durationMin);
      const endHour = Math.floor(endMinutes / 60) % 24;
      const endMin = endMinutes % 60;

      const from = `${startHour.padStart(2, "0")}:${startMin.padStart(2, "0")}`;
      const to = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;
      const slot = { from, to };

      days.split(",").forEach((day) => {
        if (!schedule[day]) schedule[day] = [];
        schedule[day].push(slot);
      });
    }
  });

  return schedule;
}

function getHourCoverage(slots: TimeSlot[]): boolean[] {
  // Array of 24 hours, true if hour is covered
  const coverage = new Array(24).fill(false);

  slots.forEach((slot) => {
    const [fromHour] = slot.from.split(":").map(Number);
    const [toHour, toMin] = slot.to.split(":").map(Number);

    const startHour = fromHour;
    const endHour = toMin > 0 ? toHour : toHour - 1;

    for (let h = startHour; h <= endHour && h < 24; h++) {
      coverage[h] = true;
    }
  });

  return coverage;
}

function formatTimeSlots(slots: TimeSlot[], closed: string): string {
  if (!slots.length) return closed;
  return slots.map((s) => `${s.from}-${s.to}`).join(", ");
}

function groupConsecutiveDays(schedule: WeekSchedule): Array<{
  startDay: number;
  endDay: number;
  slots: TimeSlot[];
}> {
  const groups: Array<{
    startDay: number;
    endDay: number;
    slots: TimeSlot[];
  }> = [];

  let currentGroup: {
    startDay: number;
    endDay: number;
    slots: TimeSlot[];
  } | null = null;

  DAYS.forEach((day, idx) => {
    const slots = schedule[day] || [];
    const slotsKey = JSON.stringify(slots);

    if (!currentGroup || JSON.stringify(currentGroup.slots) !== slotsKey) {
      // Start a new group
      if (currentGroup) {
        groups.push(currentGroup);
      }
      currentGroup = {
        startDay: idx,
        endDay: idx,
        slots: slots,
      };
    } else {
      // Extend current group
      currentGroup.endDay = idx;
    }
  });

  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}

export function ScheduleGrid({ rrules, className }: ScheduleGridProps) {
  const { t } = useTranslation();
  const locale = useDateFnsLocale();
  // "lun." / "Mon": `DAYS` starts on Monday, date-fns counts from Sunday.
  const dayLabel = (idx: number) =>
    locale.localize.day(((idx + 1) % 7) as Day, { width: "abbreviated" });
  const schedule = parseRRule(rrules);
  const groups = groupConsecutiveDays(schedule);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn("space-y-px", className)}>
          {DAYS.map((day) => {
            const slots = schedule[day] || [];
            const coverage = getHourCoverage(slots);

            return (
              <div className="flex gap-px" key={day}>
                {coverage.map((isOpen, hour) => (
                  <div
                    key={hour}
                    className={cn(
                      "h-1 w-1 rounded-full",
                      isOpen ? "bg-pink-500" : "bg-slate-500/25",
                    )}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs space-y-0.5">
        {groups.map((group, idx) => {
          const dayRange =
            group.startDay === group.endDay
              ? dayLabel(group.startDay)
              : `${dayLabel(group.startDay)}→${dayLabel(group.endDay)}`;

          return (
            <div key={idx}>
              <span className="font-medium">{dayRange}</span>:{" "}
              {formatTimeSlots(group.slots, t("schedules.days.closed"))}
            </div>
          );
        })}
      </TooltipContent>
    </Tooltip>
  );
}
